import type { GraphQLFormattedError } from 'graphql';
import { fromPromise } from 'xstate';
import type { ApolloClient } from '@apollo/client';
import type { QuickSearchResult } from '@frameio/graphql-client/generated';
import { log } from '@frameio/utils';
import { GetQuickSearchResultsNlp } from '../gql/generated/GetQuickSearchResultsNlp';
import { GetQuickSearchResultsV2 } from '../gql/generated/GetQuickSearchResultsV2';
import type { QuickSearchBaseContext } from './types';

export const sharedGuards = {
  isLexicalComplete: ({ context }: { context: QuickSearchBaseContext }) =>
    context.lexicalComplete,
};

/**
 * Handles response from quick search queries with errorPolicy: 'all'

 * @param nodes - Array of nodes to return
 * @param errors - The errors to log
 * @param queryName - The name of the query
 * @returns nodes array or throws an error
 */
export function handleQuickSearchResponse<T extends QuickSearchResult[]>(
  nodes: T,
  errors: ReadonlyArray<GraphQLFormattedError> | undefined,
  queryName: string
): T {
  if (!errors?.length) return nodes;
  if (nodes.length > 0) {
    log.warn(`[quickSearch] ${queryName} partial failure`, errors);
    return nodes;
  }
  throw new Error(errors[0]?.message ?? `${queryName} failed`);
}

export const fetchLexicalQueryActor = fromPromise<
  QuickSearchResult[],
  {
    apolloClient: ApolloClient<unknown>;
    requestVariables: QuickSearchBaseContext['requestVariables'];
  }
>(async ({ input }) => {
  const result = await input.apolloClient.query({
    query: GetQuickSearchResultsV2,
    variables: input.requestVariables,
    fetchPolicy: 'network-only',
    errorPolicy: 'all',
  });

  const nodes = (result.data?.account?.quickSearch?.nodes ??
    []) as QuickSearchResult[];

  return handleQuickSearchResponse(
    nodes,
    result.errors,
    'GetQuickSearchResultsV2'
  );
});

export const fetchNlpQueryActor = fromPromise<
  QuickSearchResult[],
  {
    apolloClient: ApolloClient<unknown>;
    requestVariables: QuickSearchBaseContext['requestVariables'];
  }
>(async ({ input }) => {
  const result = await input.apolloClient.query({
    query: GetQuickSearchResultsNlp,
    variables: input.requestVariables,
    fetchPolicy: 'network-only',
    errorPolicy: 'all',
  });

  const nodes = (result.data?.account?.quickSearchNlp?.nodes ??
    []) as QuickSearchResult[];

  return handleQuickSearchResponse(
    nodes,
    result.errors,
    'GetQuickSearchResultsNlp'
  );
});
